import { useCallback, useEffect, useRef, useState } from 'react';
import {
  findTextById,
  forceCorrectTextOrientation,
  forceScaleSync,
  intercept3DUpdates,
  preserveTextScale,
  syncTextState,
} from '../../../js/text/textUtils';
// Font loading is now handled by useFontLoader hook
import { FONT_CONFIG } from '../../../constants';
import {
  getLogoScale,
  SCALE_CONSTANTS,
  scaleToSlider,
  setLogoScale,
  sliderToScale,
} from '../../../js/text/scaleUtils';
import {
  clearText,
  deleteLogoText,
  renderTextImage,
  renderTextOnly,
} from '../../../js/textHandling';
import { updateCurrentLogo, updateLogoChange } from '../../../js/webGL';
import { useFontLoader } from './useFontLoader';

// Helper function to map font name to font-family string
const getFontFamilyFromName = (fontName) => {
  if (!fontName) return 'Arial';
  // If it's already a font-family string (contains comma), return as is
  if (fontName.includes(',')) {
    return fontName;
  }
  // Find matching font in FONT_CONFIG
  const fontOption = FONT_CONFIG.find((f) => f.name === fontName);
  if (fontOption) {
    return fontOption.family;
  }
  // If not found, return the font name as is (for system fonts like "Arial")
  return fontName;
};

export const useTextEditor = () => {
  const { loadFont } = useFontLoader();

  const inputRef = useRef(null);
  const [text, setText] = useState('');
  const [color, setColor] = useState('#000000');
  const [fontFamily, setFontFamily] = useState('Basic');
  const [scale, setScale] = useState(SCALE_CONSTANTS.DEFAULT);
  const [rotate, setRotate] = useState(0);
  const [textList, setTextList] = useState([]);
  const [activeTextId, setActiveTextId] = useState('');
  const [showFloatingInput, setShowFloatingInput] = useState(false);
  const [shouldMaintainFocus, setShouldMaintainFocus] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submissionTimeoutRef = useRef(null);
  const cleanupFunctionsRef = useRef([]);
  const textListRef = useRef(textList);
  const showFloatingInputRef = useRef(showFloatingInput);
  const activeTextIdRef = useRef(activeTextId);
  const shouldMaintainFocusRef = useRef(shouldMaintainFocus);
  const textRef = useRef(text);
  const colorRef = useRef(color);
  const fontFamilyRef = useRef(fontFamily);
  const scaleRef = useRef(scale);
  const rotateRef = useRef(rotate);

  useEffect(() => {
    textListRef.current = textList;
    showFloatingInputRef.current = showFloatingInput;
    activeTextIdRef.current = activeTextId;
    shouldMaintainFocusRef.current = shouldMaintainFocus;
    textRef.current = text;
    colorRef.current = color;
    fontFamilyRef.current = fontFamily;
    scaleRef.current = scale;
    rotateRef.current = rotate;
  }, [
    textList,
    showFloatingInput,
    activeTextId,
    shouldMaintainFocus,
    text,
    color,
    fontFamily,
    scale,
    rotate,
  ]);

  useEffect(() => {
    if (activeTextId) {
      window.textEditor_activeTextId = activeTextId;
      if (window.textEditor_state) {
        window.textEditor_state.activeTextId = activeTextId;
      }
    }
  }, [activeTextId]);

  useEffect(() => {
    if (textList.length > 0) {
      window.textEditor_textList = textList;
      if (window.textEditor_state) {
        window.textEditor_state.textList = textList;
      }
    }
  }, [textList]);

  useEffect(() => {
    if (!window.textEditor_state) {
      window.textEditor_state = {
        activeTextId: null,
        textList: [],
        initialized: true,
      };
    }
  }, []);

  useEffect(() => {
    const savedActiveTextId = window.textEditor_activeTextId;
    const savedTextList = window.textEditor_textList;

    if (savedActiveTextId && savedActiveTextId !== activeTextIdRef.current) {
      setActiveTextId(savedActiveTextId);
    }

    if (savedTextList?.length > 0 && textListRef.current.length === 0) {
      setTextList(savedTextList);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (submissionTimeoutRef.current) {
        clearTimeout(submissionTimeoutRef.current);
      }

      const globalFunctions = ['handleNewText', 'handleReset', 'handleDeleteText', 'deselectText'];
      globalFunctions.forEach((name) => {
        if (window[name]) {
          delete window[name];
        }
      });
    };
  }, []);

  const findTextByIdMemo = useCallback((id) => findTextById(textListRef.current, id), []);

  const focusInput = useCallback(() => {
    if (inputRef.current && showFloatingInputRef.current) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          if (inputRef.current.setSelectionRange) {
            const length = inputRef.current.value.length;
            inputRef.current.setSelectionRange(length, length);
          }
        }
      }, 100);
    }
  }, []);

  const forceClearInput = () => {
    setText('');
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const maintainFocus = useCallback(() => {
    setShouldMaintainFocus(true);
    setTimeout(() => {
      if (shouldMaintainFocusRef.current) {
        focusInput();
        setShouldMaintainFocus(false);
      }
    }, 100);
  }, [focusInput]);

  const preventScaleReset = useCallback(() => {
    if (activeTextIdRef.current && textListRef.current.length) {
      const logo = findTextByIdMemo(activeTextIdRef.current);
      if (logo) {
        const currentScale = logo.Scale || SCALE_CONSTANTS.DEFAULT;
        const currentSliderValue = currentScale;
        setScale(currentSliderValue);
        preserveTextScale(activeTextIdRef.current, findTextByIdMemo);
      }
    }
  }, [findTextByIdMemo]);

  // Handle text submission
  const handleSubmit = async () => {
    if (!textRef.current.trim() || isSubmitting) return;
    setIsSubmitting(true);
    if (submissionTimeoutRef.current) {
      clearTimeout(submissionTimeoutRef.current);
    }
    const textToSubmit = textRef.current.trim();
    if (textListRef.current.some((item) => item.Text === textToSubmit && item.isNew === true)) {
      setIsSubmitting(false);
      return;
    }

    // Extract font name from font-family string and ensure it's loaded
    let fontName = fontFamilyRef.current;
    if (fontName.includes(',')) {
      fontName = fontName.split(',')[0].trim();
    }
    // Load font before rendering to ensure consistency across iOS and Android
    const fontWithFallback = await loadFont(fontName);

    const uniqueId = `text_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const textPattern = {
      Id: uniqueId,
      Color: colorRef.current,
      Text: textToSubmit,
      Font: fontWithFallback,
      IsText: true,
      Scale: scaleRef.current,
      Rotate: rotateRef.current,
      isNew: true,
      Position: null,
      createdAt: Date.now(),
    };
    forceClearInput();
    setTextList((prev) => [textPattern, ...prev]);
    setShowFloatingInput(false);
    setActiveTextId(uniqueId);
    console.log('handleSubmit', uniqueId);
    renderTextOnly(textPattern);
    textPattern.isNew = false;
    updateLogoChange(textPattern);
    // Update the text pattern to mark it as placed
    setTextList((prev) =>
      prev.map((item) => (item.Id === uniqueId ? { ...item, isNew: false, Position: null } : item)),
    );
    setIsSubmitting(false);
  };

  const handleFontChange = async (font) => {
    // Extract font name from font-family string (e.g., "Inter, sans-serif" -> "Inter")
    // or use the font directly if it's already a font name
    let fontName = font;
    if (font.includes(',')) {
      fontName = font.split(',')[0].trim();
    }

    const fontWithFallback = await loadFont(fontName);

    // Store the original font-family string in state for UI comparison
    // This ensures the comparison in the UI works correctly
    setFontFamily(font);
    fontFamilyRef.current = font;

    if (activeTextIdRef.current && textListRef.current.length && window.currentLogo) {
      const currentId = activeTextIdRef.current;

      // Update the logo inside textList/textListRef so all references share the same object
      setTextList((prev) =>
        prev.map((item) => {
          if (item.Id !== currentId) return item;
          // Mutate the existing item so meshes that hold a reference stay in sync
          item.Font = fontWithFallback;
          return item;
        }),
      );

      const logoFromRef = textListRef.current.find((item) => item.Id === currentId);

      // Update window.currentLogo in-place to keep webGL.js in sync
      if (window.currentLogo && window.currentLogo.Id === currentId) {
        window.currentLogo.Font = fontWithFallback;
      }

      const targetLogo = window.currentLogo || logoFromRef;

      if (targetLogo) {
        // Re-render text with the latest text/color but new font
        targetLogo.Text = textRef.current.trim() || targetLogo.Text;
        targetLogo.Color = colorRef.current || targetLogo.Color;
        renderTextOnly(targetLogo);
      }
    }
    maintainFocus();
  };

  const handleColorChange = (newColor) => {
    if (newColor === color) return;

    setColor(newColor);

    if (activeTextIdRef.current && textListRef.current.length) {
      if (window.currentLogo) {
        window.currentLogo.Color = newColor;
        // Update the 3D scene
        renderTextOnly(window.currentLogo);
      }
    }
  };

  const handleScaleChange = (value) => {
    setScale(value);
    const actualScale = sliderToScale(value);
    updateCurrentLogo(actualScale, rotate);
  };

  const handleRotateChange = (value) => {
    setRotate(value);
    updateCurrentLogo(scale, value);
  };

  const handleDeleteText = (textId) => {
    const deleteText = window.decals.find((item) => item.logo.Id === textId);
    window.decals = window.decals.filter((item) => item.logo.Id !== textId);
    deleteLogoText(deleteText.logo);
  };

  const handleUpdateText = () => {
    if (!activeTextIdRef.current || !textRef.current.trim()) return;

    const logo = findTextByIdMemo(activeTextIdRef.current);
    if (logo) {
      // Update the logo object with new text, color, and font
      const updatedText = textRef.current.trim();
      const updatedColor = colorRef.current;
      const updatedFont = fontFamilyRef.current;

      // Mutate the existing logo so all references (webGL, decals, etc.) see the same data
      logo.Text = updatedText;
      logo.Color = updatedColor;
      logo.Font = updatedFont;

      // Keep window.currentLogo in sync if it's the same text decal
      if (window.currentLogo && window.currentLogo.Id === logo.Id) {
        window.currentLogo.Text = updatedText;
        window.currentLogo.Color = updatedColor;
        window.currentLogo.Font = updatedFont;
      }

      // Update the 3D scene
      renderTextOnly(window.currentLogo || logo);

      // Update the local state
      setTextList((prev) =>
        prev.map((item) => (item.Id === activeTextIdRef.current ? logo : item)),
      );

      // Clear the input but keep text selected
      setText('');
      // Keep activeTextId to maintain selection
      setShowFloatingInput(false);

      // Force clear input
      forceClearInput();
    }
  };

  const handleClearText = (textId) => {
    const logo = findTextByIdMemo(textId);
    if (logo) {
      logo.Position = null;
      logo.isNew = true;
      clearText(logo);
      // setTextList((prev) => [...prev]); // Force re-render - Commented out to prevent infinite loops
    }
  };

  const handleTextClick = (textItem, index) => {
    setActiveTextId(textItem.Id);
    setText(textItem.Text);
    setColor(textItem.Color);
    setFontFamily(getFontFamilyFromName(textItem.Font));

    // Use scale utility functions for safe scale handling
    let actualScale = getLogoScale(textItem);

    // Set the scale state to match the actual scale (convert to slider range 2-100)
    const sliderValue = scaleToSlider(actualScale);
    setScale(sliderValue);
    setRotate(textItem.Rotate || 0);
    setShowFloatingInput(false); // Show input when selecting text - REMOVED

    // Don't re-render the text when selecting - just update the UI state
    // This prevents text reversal and maintains the current 3D scene state
    // CRITICAL: Ensure the scale is properly synchronized
    setTimeout(() => {
      // CRITICAL: Force complete scale synchronization
      forceScaleSync(textItem.Id, findTextByIdMemo, setScale, setTextList);
      syncTextState(textItem.Id, findTextByIdMemo); // Sync state with 3D scene
      forceCorrectTextOrientation(textItem.Id, findTextByIdMemo);
      focusInput();
    }, 100);
  };

  const handleDragStart = (textItem) => {
    if (!textItem.isNew) return;
    renderTextOnly(textItem);
    setActiveTextId(textItem.Id);
  };

  const handleReset = () => {
    if (activeTextIdRef.current) {
      const logo = findTextByIdMemo(activeTextIdRef.current);
      if (logo) {
        // Use scale utility functions for safe scale reset
        setLogoScale(logo, SCALE_CONSTANTS.DEFAULT); // Reset to minimum scale (1)
        logo.Rotate = 0;
        logo.Color = '#FFFFFF';
        setScale(scaleToSlider(SCALE_CONSTANTS.DEFAULT)); // Set slider to minimum position
        setRotate(0);
        setColor('#FFFFFF');
        updateLogoChange(logo);
      }
    }
  };

  const handleNewText = () => {
    // Clear current text and reset all text-related state to default values
    forceClearInput();
    setText(''); // Reset text to empty
    setColor('#000000'); // Reset color to default black
    setFontFamily('Basic'); // Reset font family to default
    setScale(SCALE_CONSTANTS.DEFAULT); // Reset scale to default (20 represents scale = 10)
    setRotate(0); // Reset rotation to 0
    setActiveTextId(''); // Clear active text selection
    setShowFloatingInput(true); // Show input for new text
    setIsSubmitting(false); // Reset submission state

    // Update refs to match the reset state
    textRef.current = '';
    colorRef.current = '#000000';
    fontFamilyRef.current = 'Basic';
    scaleRef.current = 20;
    rotateRef.current = 0;
    activeTextIdRef.current = '';

    // Focus input after a short delay to ensure DOM is updated
    setTimeout(() => {
      focusInput();
    }, 100);
  };

  // Global functions for external access
  const updateTheLogoText = useCallback((logoTarget) => {
    if (logoTarget.IsText === true) {
      setTextList((prev) => {
        const found = prev.find(
          (logo) => logo.Id === logoTarget.Id && logo.Text === logoTarget.Text,
        );
        if (!found) {
          logoTarget.isNew = true;
          return [...prev, logoTarget];
        } else {
          found.isNew = false;
          return [...prev];
        }
      });
    }
  }, []);

  const selectTextDecals = (textDecal) => {
    // Check if this is a new text selection (different from currently active)
    const isNewSelection = activeTextIdRef.current !== textDecal.Id;

    setText(textDecal.Text);
    setColor(textDecal.Color);
    setFontFamily(getFontFamilyFromName(textDecal.Font));
    setActiveTextId(textDecal.Id);

    activeTextIdRef.current = textDecal.Id;
    // Ensure the selected text exists in the list for downstream handlers
    const existsInRef = textListRef.current.some((item) => item.Id === textDecal.Id);
    if (!existsInRef) {
      textListRef.current = [...textListRef.current, textDecal];
      setTextList((prev) => {
        const already = prev.some((item) => item.Id === textDecal.Id);
        return already ? prev : [...prev, textDecal];
      });
    } else {
      textListRef.current = textListRef.current.map((item) =>
        item.Id === textDecal.Id ? textDecal : item,
      );
    }

    // Only update scale and rotate when selecting a NEW text, not when moving the same text
    if (isNewSelection) {
      // Use scale utility functions for safe scale handling
      let actualScale = getLogoScale(textDecal);

      // Set the scale state to match the actual scale (convert to slider range 2-100)
      const sliderValue = scaleToSlider(actualScale);
      setScale(sliderValue);
      setRotate(textDecal.Rotate || 0);

      // CRITICAL: Ensure the scale is properly synchronized only for new selections
      setTimeout(() => {
        // CRITICAL: Force complete scale synchronization
        forceScaleSync(textDecal.Id, findTextByIdMemo, setScale, setTextList);
        syncTextState(textDecal.Id, findTextByIdMemo); // Sync state with 3D scene
        forceCorrectTextOrientation(textDecal.Id, findTextByIdMemo);
        focusInput();
      }, 100);
    } else {
      // If it's the same text being moved, keep the current scale state unchanged
      // Only sync state and orientation, but NOT scale
      setTimeout(() => {
        syncTextState(textDecal.Id, findTextByIdMemo); // Sync state with 3D scene
        forceCorrectTextOrientation(textDecal.Id, findTextByIdMemo);
        focusInput();
      }, 100);
    }

    // Show input and focus it when text is selected
    setShowFloatingInput(false);
  };

  const deselectText = () => {
    setActiveTextId('');
    setText('');
    setShowFloatingInput(false);
    // Clear input
    forceClearInput();
  };

  const textFontChanged = useCallback(
    (newFont) => {
      setFontFamily(newFont);
      // Access current state values through refs to avoid dependency issues
      const currentText = textRef.current;
      const currentColor = colorRef.current;
      const currentActiveTextId = activeTextIdRef.current;
      renderTextImage(currentText, currentColor, newFont, currentActiveTextId);
    },
    [], // No dependencies - uses refs to access current values
  );

  // Effects
  useEffect(() => {
    // Set up global functions - use function references to avoid dependency issues
    window.updateTheLogoText = updateTheLogoText;
    window.selectTextDecals = selectTextDecals;
    window.deselectText = deselectText;
    window.textFontChanged = textFontChanged;
    window.activeTextIdRef = activeTextIdRef;
    // Set toast message - let the parent Tool component handle this to prevent conflicts
    // dispatch(setIsDisplay(false)); // Commented out to prevent conflicts with parent
    // dispatch(setMessage("Enter text and drag it to the product to add custom text.")); // Commented out to prevent conflicts
    return () => {
      // Only clear message, don't change display state to prevent conflicts
      // dispatch(setMessage("")); // Commented out to prevent conflicts
    };
  }, []); // Include stable functions

  // Global focus restoration for any UI interaction
  useEffect(() => {
    if (shouldMaintainFocus && showFloatingInput) {
      const timer = setTimeout(() => {
        focusInput();
        setShouldMaintainFocus(false);
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [shouldMaintainFocus, showFloatingInput, focusInput]); // Include focusInput since it's stable

  // Focus input when showFloatingInput changes to true
  useEffect(() => {
    if (showFloatingInput) {
      focusInput();
    }
  }, [showFloatingInput, focusInput]);

  // Focus input when component mounts
  useEffect(() => {
    if (showFloatingInput) {
      focusInput();
    }
    // Enable 3D update interception to prevent text flipping
    // Only run once on mount, not every time textList changes
    intercept3DUpdates([]); // Pass empty array since this is just setting up global overrides
  }, [showFloatingInput, focusInput]);

  // CRITICAL: Maintain scale state when activeTextId changes
  useEffect(() => {
    if (activeTextIdRef.current && textListRef.current.length) {
      const logo = findTextByIdMemo(activeTextIdRef.current);
      if (logo) {
        // CRITICAL: Force scale synchronization when activeTextId changes
        forceScaleSync(activeTextIdRef.current, findTextByIdMemo, setScale, setTextList);
        // Additional scale preservation
        setTimeout(() => {
          preserveTextScale(activeTextIdRef.current, findTextByIdMemo);
        }, 50);
        setTimeout(() => {
          preserveTextScale(activeTextIdRef.current, findTextByIdMemo);
        }, 200);
      }
    }
  }, [activeTextId, findTextByIdMemo]);

  // Focus input when text changes (for better UX)
  useEffect(() => {
    if (text === '' && showFloatingInput) {
      // Focus input when text is cleared
      setTimeout(() => {
        focusInput();
      }, 50);
    }
  }, [text, showFloatingInput, focusInput]);

  // Ensure input is cleared when text state is empty
  useEffect(() => {
    if (text === '' && inputRef.current) {
      inputRef.current.value = '';
      // Force clear any remaining text
      if (inputRef.current.value !== '') {
        inputRef.current.value = '';
      }
    }
  }, [text]);

  // Additional cleanup effect to ensure input is cleared
  useEffect(() => {
    // Copy ref to variable inside effect to avoid stale closure
    const currentInputRef = inputRef.current;
    return () => {
      if (currentInputRef) {
        currentInputRef.value = '';
      }
    };
  }, []);

  // Mobile keyboard handling
  useEffect(() => {
    const handleResize = () => {
      const inputElement = document.getElementById('floating-text-input-mobile');
      if (inputElement) {
        inputElement.style.top = window.innerHeight < 600 ? '10%' : '25%';
      }
    };
    const handleVisualViewportChange = () => handleResize();
    const handleTouchStart = () => {
      if (inputRef.current && showFloatingInputRef.current) {
        setTimeout(() => focusInput(), 100);
      }
    };
    // Add event listeners
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('resize', handleResize);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleVisualViewportChange);
    }
    return () => {
      document.removeEventListener('touchstart', handleTouchStart, {
        passive: true,
      });
      window.removeEventListener('resize', handleResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleVisualViewportChange);
      }
    };
  }, [showFloatingInput, focusInput]);

  // Removed global event listeners to prevent infinite loops and conflicts
  // Enter key handling is now done directly in the input components

  // UI state update functions for undo/redo
  const updateTextUIState = useCallback(
    (logo) => {
      if (logo && logo.Id) {
        // Update active text ID
        setActiveTextId(logo.Id);

        // Update all UI state to match the restored logo
        setText(logo.Text || '');
        setColor(logo.Color || '#FFFFFF');
        setFontFamily(getFontFamilyFromName(logo.Font || 'Arial'));
        setScale(scaleToSlider(logo.Scale || SCALE_CONSTANTS.DEFAULT));
        setRotate(logo.Rotate || 0);
      }
    },
    [setScale],
  );

  const clearTextUIState = useCallback(() => {
    setActiveTextId(null);
    setText('');
    setColor('#FFFFFF');
    setFontFamily('Arial');
    setScale(SCALE_CONSTANTS.DEFAULT); // Default slider value
    setRotate(0);
  }, []);

  // Set up global functions for undo/redo UI updates
  useEffect(() => {
    window.updateTextUIState = updateTextUIState;
    window.clearTextUIState = clearTextUIState;
    // Cleanup on unmount
    return () => {
      // Clean up all pending timeout cleanup functions
      cleanupFunctionsRef.current.forEach((cleanupFn) => {
        try {
          if (typeof cleanupFn === 'function') cleanupFn();
        } catch (error) {}
      });
      cleanupFunctionsRef.current = [];

      // Safely clean up global functions
      const cleanupFunctions = [
        'updateTextUIState',
        'clearTextUIState',
        'doUndoText',
        'doRedoText',
        'pushTextUndoQueue',
      ];

      cleanupFunctions.forEach((funcName) => {
        try {
          const descriptor = Object.getOwnPropertyDescriptor(window, funcName);
          if (descriptor && descriptor.configurable) {
            delete window[funcName];
          } else {
            window[funcName] = undefined;
          }
        } catch (error) {
          window[funcName] = undefined;
        }
      });
    };
  }, [updateTextUIState, clearTextUIState]);

  return {
    // State
    text,
    setText,
    color,
    setColor,
    fontFamily,
    setFontFamily,
    scale,
    setScale,
    rotate,
    setRotate,
    textList,
    setTextList,
    activeTextId,
    setActiveTextId,
    showFloatingInput,
    setShowFloatingInput,
    shouldMaintainFocus,
    setShouldMaintainFocus,
    inputRef,
    // Functions
    focusInput,
    forceClearInput,
    maintainFocus,
    preventScaleReset,
    findTextByIdMemo,
    handleSubmit,
    handleFontChange,
    handleColorChange,
    handleScaleChange,
    handleRotateChange,
    handleDeleteText,
    handleUpdateText,
    handleClearText,
    handleTextClick,
    handleDragStart,
    handleReset,
    handleNewText,
    deselectText,
    // Global functions
    updateTheLogoText,
    selectTextDecals,
    textFontChanged,
  };
};
